import { useTranslation } from 'react-i18next';
import {
  PageContainer,
  PageFooter,
  PageHeader,
} from '../../../shared_components';
import {
  FieldTypes,
  parameterField,
  parameterFields,
  predefinedGoals,
} from '../config';
import {
  Accordion,
  NumberInput,
  Select,
  TextInput,
  Text,
  Space,
  Stack,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import {
  ParameterLevel,
  Parameters,
  userGoalDto,
  UserGoalDto,
} from '@web/common';
import { useKeycloakStore, useUserDataStore } from '../../../shared_stores';
import { useNavigate } from 'react-router-dom';

export const SetGoal = () => {
  const { t } = useTranslation(['common', 'reports']);
  const navigate = useNavigate();
  const kcStore = useKeycloakStore();
  const userDataStore = useUserDataStore();
  const [customGoalName, setCustomGoalName] = useState<string>();
  const [customParameters, setCustomParameters] = useState<Parameters>();
  const [customGoalValue, setCustomGoalValue] = useState<
    string | number | ParameterLevel
  >();
  const fieldValues = parameterFields.map((entry) => {
    return {
      name: entry.title,
      parameter: entry.parameter,
      value: entry.initialValue,
    };
  });
  const [fieldStates, setFieldStates] = useState(fieldValues);
  const form = useForm<UserGoalDto>({
    validate: zodResolver(userGoalDto),
  });

  function setFormValues(
    name: Parameters,
    parameter: Parameters,
    value: string | ParameterLevel | number,
  ) {
    const index = fieldStates.findIndex((entry) => entry.name === name);
    const newFieldStates = fieldStates;
    newFieldStates[index] = { name: name, parameter: parameter, value: value };
    setFieldStates(newFieldStates);
    form.setValues({
      name: name,
      parameter: parameter,
      targetValue: value,
    });
  }

  function setCustomGoalFormValues(value: string | number | ParameterLevel) {
    setCustomGoalValue(value);
    form.setFieldValue('targetValue', value);
  }

  function getInputFromField(
    field: parameterField,
    setInitialValue = true,
    customGoalField = false,
  ) {
    switch (field.type) {
      case FieldTypes.DROPDOWN:
        return (
          <Select
            label={t(`reports:${field.title}`)}
            data={Object.values(ParameterLevel).map((value) => ({
              value: value,
              label: t(`reports:${value}`),
            }))}
            placeholder={
              t(`reports:${field.title}`) + t('common:placeholder_enter')
            }
            defaultValue={
              setInitialValue ? field.initialValue?.toString() : undefined
            }
            onChange={(option) => {
              customGoalField
                ? setCustomGoalFormValues(option as ParameterLevel)
                : setFormValues(
                    field.title,
                    field.parameter,
                    option as ParameterLevel,
                  );
            }}
          />
        );
      case FieldTypes.TEXT:
        return (
          <TextInput
            label={t(`reports:${field.title}`)}
            placeholder={
              t(`reports:${field.title}`) + t('common:placeholder_enter')
            }
            defaultValue={setInitialValue ? field.initialValue : undefined}
            onChange={(event) => {
              customGoalField
                ? setCustomGoalFormValues(event.currentTarget.value)
                : setFormValues(
                    field.title,
                    field.parameter,
                    event.currentTarget.value,
                  );
            }}
          />
        );
      case FieldTypes.NUMBER:
        return (
          <NumberInput
            label={t(`reports:${field.title}`)}
            placeholder={
              t(`reports:${field.title}`) + t('common:placeholder_enter')
            }
            defaultValue={setInitialValue ? field.initialValue : undefined}
            onChange={(value) => {
              customGoalField
                ? setCustomGoalFormValues(value)
                : setFormValues(field.title, field.parameter, value);
            }}
          />
        );
      default:
        return null;
    }
  }

  const items = predefinedGoals.map((goal) => {
    if (goal.field) {
      return (
        <Accordion.Item key={goal.title} value={goal.title}>
          <Accordion.Control
            onClick={() => {
              form.setValues({
                name: goal.title,
                parameter: goal.field?.parameter,
                targetValue: fieldStates.find(
                  (entry) => entry.name === goal.field?.title,
                )?.value,
              });
            }}
          >
            {t(`reports:${goal.title}`)}
          </Accordion.Control>
          <Accordion.Panel>
            <Text>{t('reports:goal_description')}</Text>
            <Space h={'var(--mantine-spacing-md)'} />
            {getInputFromField(goal.field)}
          </Accordion.Panel>
        </Accordion.Item>
      );
    }
    return null;
  });

  const customParameterField = () => {
    return parameterFields.map((entry) => {
      return (
        <div
          style={
            entry.title === customParameters
              ? { display: 'block' }
              : { display: 'none' }
          }
        >
          {getInputFromField(entry, false, true)}
        </div>
      );
    });
  };

  const handleSubmit = async () => {
    try {
      await userDataStore.setGoal(kcStore.token, form.getValues());
      void navigate('/reports/dashboard');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <PageContainer hasFooter>
      <form onSubmit={form.onSubmit(handleSubmit, console.log)}>
        <PageHeader lowMargin title={t('reports:set_goal_title')} />

        <Accordion>
          {items}
          <Accordion.Item key={'custom_goal'} value={'custom_goal'}>
            <Accordion.Control
              onClick={() => {
                form.setValues({
                  name: customGoalName,
                  parameter: customParameters,
                  targetValue: customGoalValue,
                });
                console.log(form.getValues());
              }}
            >
              {t(`reports:custom_goal`)}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={'var(--mantine-spacing-xs)'}>
                <Text>{t('reports:custom_goal_description')}</Text>
                <TextInput
                  label={t('reports:custom_goal_name')}
                  placeholder={
                    t('reports:custom_goal_name') +
                    t('common:placeholder_enter')
                  }
                  onChange={(event) => {
                    setCustomGoalName(event.currentTarget.value);
                    form.setFieldValue('name', event.currentTarget.value);
                  }}
                />
                <Select
                  label={t('reports:parameter')}
                  placeholder={t('common:select_placeholder')}
                  data={parameterFields.map((entry) => ({
                    value: entry.title,
                    label: t(`reports:${entry.title}`),
                  }))}
                  onChange={(option) => {
                    setCustomParameters(option as Parameters);
                    form.setFieldValue('parameter', option as Parameters);
                  }}
                />
                {customParameterField()}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
        <PageFooter
          leftOnClick={() => window.history.back()}
          buttonText={t('common:save_changes')}
          submitButton
          disabled={!form.isValid()}
        />
      </form>
    </PageContainer>
  );
};
